/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  ActionIcon,
  Button,
  Flex,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea
} from "@mantine/core";
import { IconPencil, IconRefresh } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import SlackIcon from "../../../../../assets/images/slack.svg?react";
import {
  useSlackChannels,
  useSlackIntegration,
  useSlackUsers,
  useWorkspaceObjectSchema,
} from "../../../../../hooks/QueryHooks";
import MultiSelectDropdown from "../../../../../shared/ui/dropdown/MultiSelectDropdown";
import VariableChipEditor from "../../../../../shared/ui/VariableChipEditor";
import type { VariableOption } from "../../../../../shared/ui/VariableChipEditor";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import notify from "../../../../../utils/notify";
import SlackPreview from "./SlackPreview";

type Props = {
  onCompleteAction: (action: any, type: string) => void;
  opened: boolean;
  close: () => void;
  data?: any;
  setSelectedFields?: any;
  setSelectedMetrics: any;
  signal?: any;
};

const assignedChoices = [
  {
    value: "",
    label: "None",
  },
  {
    value: "csm",
    label: "Assigned CSM",
  },
  {
    value: "ae",
    label: "Assigned AE",
  },
];

const SlackModal = ({
  onCompleteAction,
  opened,
  close,
  data,
  setSelectedFields,
  signal,
  setSelectedMetrics,
}: Props) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const [showHidden, setShowHidden] = useState(false);
  const [traitType, setTraitType] = useState<"account" | "user">("account");
  // const [radio, setRadio] = useState(data?.message_type === "direct" ? "direct" : "channel"); // ["channel", "direct"
  const [error, setError] = useState<boolean>(false);
  const [radio, setRadio] = useState("channel"); // ["channel", "direct"]
  const [assignedTo, setAssignedTo] = useState(data?.assign_to || "");
  const [subject, setSubject] = useState(data?.message || "");
  const [to, setTo] = useState(data?.slack_user_id || "");
  const [channel, setChannel] = useState(data?.channel_id || "");
  const [refreshChannels, setRefreshChannels] = useState(false);
  const [refreshUsers, setRefreshUsers] = useState(false);

  const workspaceSchema: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });
  const accountTraits = workspaceSchema?.data?.fieldsByObjectSlug?.accounts || { list: [], obj: {} };
  const userTraits = workspaceSchema?.data?.fieldsByObjectSlug?.users || { list: [], obj: {} };
  const accountMetrics = workspaceSchema?.data?.metricsByObjectSlug?.accounts
    || workspaceSchema?.data?.metrics
    || { list: [], obj: {} };
  const userMetrics = workspaceSchema?.data?.metricsByObjectSlug?.users
    || workspaceSchema?.data?.metrics
    || { list: [], obj: {} };

  const filteredAccountTraits = useMemo(
    () =>
      accountTraits?.list?.filter(
        (item: any) => !item.exclude_display
      ),
    [accountTraits?.list]
  );

  const filteredUserTraits = useMemo(
    () =>
      userTraits?.list?.filter(
        (item: any) => !item.exclude_display
      ),
    [userTraits?.list]
  );

  const filteredAccountMetrics = useMemo(
    () =>
      accountMetrics?.list?.filter(
        (item: any) => !item.exclude_display
      ),
    [accountMetrics?.list]
  );

  const filteredUserMetrics = useMemo(
    () =>
      userMetrics?.list?.filter(
        (item: any) => !item.exclude_display
      ),
    [userMetrics?.list]
  );

  const currentTraits = useMemo(
    () =>
      traitType === "account" ? filteredAccountTraits : filteredUserTraits,
    [traitType, filteredAccountTraits, filteredUserTraits]
  );

  const currentMetrics = useMemo(
    () =>
      signal?.entity_relation === "accounts" && traitType === "account"
        ? filteredAccountMetrics
        : signal?.entity_relation === "users" && traitType === "user"
          ? filteredUserMetrics
          : [],
    [traitType, filteredAccountMetrics, filteredUserMetrics]
  );

  // Build variable options for VariableChipEditor
  const variableOptions: VariableOption[] = useMemo(() => {
    const vars: VariableOption[] = [];
    (filteredAccountTraits || []).forEach((f: any) => {
      vars.push({ name: `accounts.${f.name}`, display_name: f.display_name || f.name, group: "Account Traits" });
    });
    (filteredAccountMetrics || []).forEach((m: any) => {
      vars.push({ name: `accounts.${m.name}`, display_name: m.display_name || m.name, group: "Account Metrics" });
    });
    return vars;
  }, [filteredAccountTraits, filteredAccountMetrics]);

  const onSelectColumn = (item: any) => {
    if (item?.attribute_type?.includes("metric")) {
      setSelectedMetrics(
        signal?.metrics.includes(item.name)
          ? signal?.metrics.filter((name: any) => name !== item.name)
          : [...signal.metrics, item.name]
      );
    } else {
      setSelectedFields(
        signal?.selected_fields.includes(item.name)
          ? signal?.selected_fields.filter((name: any) => name !== item.name)
          : [...signal.selected_fields, item.name]
      );
    }
  };

  const { isError, isSettled }: any = useSlackIntegration({
    workspaceId: workspace.id,
    enabled: true,
    retry: false,
  });

  const {
    mutate: fetchUsers,
    data: usersResponse,
    isLoading: isLoadingUsers,
  } = useSlackUsers();

  const {
    mutate: fetchChannels,
    data: channelsResponse,
    isLoading: isLoadingChannels,
  } = useSlackChannels();

  const users = usersResponse?.data?.users?.map((item: any) => ({
    value: item?.id,
    label: item?.name,
  }));

  const channels = channelsResponse?.data?.channels?.map((item: any) => ({
    value: item?.id,
    label: item?.normalised_name,
  }));

  // Initial data fetch when workspace is available
  useEffect(() => {
    if (workspace?.id && !isError) {
      fetchUsers({ workspace_id: workspace.id, refresh: false });
      fetchChannels({ workspace_id: workspace.id, refresh: false });
    }
  }, [workspace?.id, isError, fetchUsers, fetchChannels]);

  useEffect(() => {
    if (data?.message_type === "direct") {
      setRadio("direct");
    } else {
      setRadio("channel");
    }
    if (data?.assign_to) {
      setAssignedTo(data?.assign_to);
    }
    if (data?.message) {
      setSubject(data?.message);
    }
    if (data?.slack_user_id) {
      setTo(data?.slack_user_id);
    }
    if (data?.channel_id) {
      setChannel(data?.channel_id);
    }
  }, [data]);

  return (
    <Modal
      padding="lg"
      classNames={{
        title: "text-gray-900 text-xl font-bold",
      }}
      size={700}
      opened={opened}
      centered
      onClose={() => {
        close();
      }}
      title={
        <Flex align="center">
          <SlackIcon className="w-6 h-6 mr-4 text-gray-600" />
          <Stack gap={0}>
            <Text>Slack | Send message</Text>
            <Text size="xs" c="text.5" fw={500}>
              Send a message to a Slack user or channel.
            </Text>
          </Stack>
        </Flex>
      }
    >
      {radio === "channel" ? (
        <div className="w-full">
          <Flex gap="xs">
            <Select
              m={0}
              classNames={{
                label: "text-gray-500 font-bold text-xs",
              }}
              className="flex-1"
              data={channels || []}
              label="Select Channel"
              size="sm"
              placeholder={isLoadingChannels ? "Loading channels..." : "Choose Channel"}
              searchable
              disabled={isLoadingChannels}
              onChange={(value: any) => {
                setChannel(value);
              }}
              value={channel}
              error={error && channel === "" ? "You must select a channel" : null}
            />
            <ActionIcon
              size="lg"
              mt={23}
              variant="outline"
              color="main.5"
              loading={refreshChannels || isLoadingChannels}
              onClick={() => {
                setRefreshChannels(true);
                fetchChannels(
                  { workspace_id: workspace.id, refresh: true },
                  {
                    onSettled: () => setRefreshChannels(false),
                  }
                );
              }}
              title="Refresh channels"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Flex>
        </div>
      ) : (
        <>
          <div className="w-full">
            <Flex gap="xs">
              <Select
                classNames={{
                  label: "text-gray-500 font-bold text-xs",
                }}
                className="flex-1"
                data={users || []}
                label="Select User"
                size="sm"
                placeholder={isLoadingUsers ? "Loading users..." : "Choose User"}
                searchable
                disabled={isLoadingUsers}
                onChange={(value: any) => {
                  setTo(value);
                }}
                value={to}
                error={error && to === "" ? "You must select a user" : null}
              />
              <ActionIcon
                mt={23}
                size="lg"
                variant="outline"
                color="main.5"
                loading={refreshUsers || isLoadingUsers}
                onClick={() => {
                  setRefreshUsers(true);
                  fetchUsers(
                    { workspace_id: workspace.id, refresh: true },
                    {
                      onSettled: () => setRefreshUsers(false),
                    }
                  );
                }}
                title="Refresh users"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Flex>
          </div>
          <div className="w-full">
            <Flex align="end" gap="xs">
              <Select
                classNames={{
                  label: "text-gray-500 font-bold mb-1 text-xs",
                }}
                className="flex-1"
                data={channels || []}
                label="Select fallback channel"
                size="sm"
                placeholder={isLoadingChannels ? "Loading channels..." : "Choose Channel"}
                searchable
                disabled={isLoadingChannels}
                onChange={(value: any) => {
                  setChannel(value);
                }}
                value={channel}
                error={
                  error && channel === ""
                    ? "You must select a fallback channel"
                    : null
                }
              />
              <ActionIcon
                size="sm"
                variant="outline"
                color="purple"
                loading={refreshChannels || isLoadingChannels}
                onClick={() => {
                  setRefreshChannels(true);
                  fetchChannels(
                    { workspace_id: workspace.id, refresh: true },
                    {
                      onSettled: () => setRefreshChannels(false),
                    }
                  );
                }}
                title="Refresh channels"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Flex>
          </div>
        </>
      )}
      <Select
        onChange={(value: any) => {
          setAssignedTo(value);
        }}
        value={assignedTo}
        size="sm"
        classNames={{
          label: "text-gray-500 font-bold mb-1 text-xs",
        }}
        placeholder="Please select"
        data={assignedChoices}
        label="Assigned to"
      />
      <div>
        <Text size="xs" fw={700} c="gray.5" mb={4}>Message</Text>
        <VariableChipEditor
          value={subject}
          onChange={setSubject}
          variables={variableOptions}
          placeholder="Enter an alert message with variables like {{accounts.arr}}"
          rows={3}
        />
      </div>
      <MultiSelectDropdown
        selected={[...signal.selected_fields, ...signal.metrics]}
        setSelected={onSelectColumn}
        isOpen={showHidden}
        setIsOpen={setShowHidden}
        list={currentTraits}
        metricsList={currentMetrics}
        position="bottom-end"
      >
        <Stack gap="xs">
          <SegmentedControl
            value={traitType}
            onChange={(value: any) => {
              setTraitType(value);
              setShowHidden(true);
            }}
            data={[
              { label: "Account Traits", value: "account" },
              { label: "User Traits", value: "user" },
            ]}
            className="mx-auto mt-[10px]"
            size="xs"
            color="main.5"
          />
          <Button
            color="white"
            size="sm"
            onClick={() => {
              setShowHidden((state) => !state);
            }}
            leftSection={<IconPencil className="w-4 h-4" />}
            w="100%"
          >
            Select Additional Traits
          </Button>
        </Stack>
      </MultiSelectDropdown>
      <Text className="text-gray-500 font-bold text-xs my-2">Preview</Text>
      <Paper withBorder className="bg-gray-50" shadow="xs">
        <SlackPreview
          signal={signal}
          traits={{
            ...accountTraits?.obj,
            ...userTraits?.obj,
          }}
          metrics={
            signal?.entity_relation === "accounts"
              ? accountMetrics?.obj
              : userMetrics?.obj
          }
        />
        {/* <Flex p="md" align="center" gap={5} className="w-full bg-purple-100">
          <SlackIcon className="w-4 h-4 mr-2 text-gray-600" />
          <Badge color="main.5">Hyperengage Alert</Badge>
          <Text className="ml-auto text-xs text-gray-500">{format(new Date(), "hh:mm a")}</Text>
        </Flex>
        <Text p="md" lineClamp={4} size="sm" c="text.5" className="border-t border-gray-300">{subject || "Enter a slack message"}</Text> */}
      </Paper>
      <Flex justify="flex-end" gap={5} className="mt-4">
        <Button
          onClick={() => {
            close();
          }}
          variant="outline"
          className="hover:bg-red-100"
          color="red"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (subject !== "") {
              if (radio === "channel" && channel !== "") {
                onCompleteAction(
                  {
                    type: "send_to_slack",
                    message_type: "channel",
                    assign_to: assignedTo || null,
                    channel_id: channel,
                    message: subject || "",
                  },
                  "send_to_slack"
                );
              } else if (radio === "direct" && to !== "" && channel !== "") {
                onCompleteAction(
                  {
                    type: "send_to_slack",
                    message_type: "direct",
                    assign_to: assignedTo || null,
                    slack_user_id: to,
                    channel_id: channel,
                    message: subject || "",
                  },
                  "send_to_slack"
                );
              } else {
                setError(true);
                return;
              }
              notify({
                messageList: ["Slack action added successfully"],
                type: "info",
              });
              close();
            } else {
              setError(true);
            }
          }}
        >
          Save Action
        </Button>
      </Flex>
    </Modal>
  );
};

export default SlackModal;
